import { AlertTriangle, Check, CreditCard, Download, Inbox, Layout, Mail, Megaphone, MessageSquare, PlugZap, Rocket, RotateCcw, Send, Settings, Share2, StickyNote, Users, X } from "lucide-react";
import { toast } from "sonner";

import { CreditBalanceBannerPreview } from "@/components/layout/CreditBalanceBanner";
import { setDisconnectedAccountPreview } from "@/components/layout/DisconnectedAccountBanner";
import AllFieldTypesPreview from "@/components/shared/AllFieldTypesPreview";
import { CreditLimitWarningDialogPreview } from "@/components/shared/CreditLimitWarningDialog";
import { type CustomerEmailType } from "@/components/shared/CustomerEmailPreview";
import FreePlanIntegrationsPreview from "@/components/shared/FreePlanIntegrationsPreview";
import { FEATURE_BANNER_RESET_EVENT } from "@/components/shared/NewFeatureBanner";
import { FreePlanRetentionBannerPreview } from "@/components/shared/FreePlanRetentionBanner";
import { FreePlanSeatLimitDialogPreview } from "@/components/shared/FreePlanSeatLimitDialog";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { isLovablePreview, setPreviewMode } from "@/lib/utils/environment";

/**
 * The hidden "UI" tab in PersonDetailSheet, revealed by triple-clicking the
 * avatar. It is an internal gallery for the team — every banner, dialog, empty
 * state, onboarding flow and customer email the app can render, linked in one
 * place for design review and QA.
 *
 * It is deliberately EXEMPT from the i18n lint (see `eslint.i18n-exempt.json`).
 * The copy here names internal surfaces and describes them to whoever is
 * testing; it is not product copy a customer reads, and translating "Brew Notes
 * June, 2026" into ten languages would be noise in every catalog. It was split
 * out of PersonDetailSheet so that exemption can be a whole file rather than a
 * 480-line hole in the middle of a translated one.
 */
export default function PersonSheetUiElementsPanel({
  onPreviewEmail,
}: {
  onPreviewEmail: (type: CustomerEmailType) => void;
}) {
  return (
                <TabsContent value="ui-elements" className="space-y-6">
                  <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4">
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><RotateCcw className="h-4 w-4 text-yellow-600" /> Preview Mode</h3>
                    <p className="text-muted-foreground text-xs mb-3">
                      {isLovablePreview()
                        ? "Currently in preview mode (dummy data). Switch to production mode to use real backend data."
                        : "Currently in production mode (real data). Switch to preview mode to use dummy data for demos."}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 border-yellow-500/40 hover:bg-yellow-500/10"
                      onClick={() => {
                        const host = window.location.hostname;
                        const isCoffeeHost = host === "demo.coffee.ai" || host === "fresh.coffee.ai";
                        if (isLovablePreview()) {
                          // Switch to production: explicit toggle off (honored persistently)
                          setPreviewMode(false);
                          toast.success("Switching to production mode — reloading...");
                          setTimeout(() => {
                            window.location.href = isCoffeeHost ? "https://fresh.coffee.ai/" : "/";
                          }, 1500);
                        } else {
                          // Switch to preview: explicit toggle on (honored persistently)
                          setPreviewMode(true);
                          toast.success("Switching to preview mode — reloading...");
                          setTimeout(() => {
                            window.location.href = isCoffeeHost ? "https://demo.coffee.ai/" : "/";
                          }, 1500);
                        }
                      }}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      {isLovablePreview() ? "Switch to Production" : "Switch to Preview"}
                    </Button>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> 404 Page</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview the 404 error page.</p>
                    <Button variant="outline" size="sm" asChild><a href="/this-page-does-not-exist" target="_blank" rel="noopener noreferrer">Open 404 Page</a></Button>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Layout className="h-4 w-4" /> Drill-down field types</h3>
                    <p className="text-muted-foreground text-xs mb-3">
                      Sample person drill-down card populated with one row for every supported field type — text, number, currency, date, checkbox, single/multi-select, email, location, phone, traffic light, URL, AI, and formula. Values are interactive so you can preview each editor.
                    </p>
                    <AllFieldTypesPreview />
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Users className="h-4 w-4" /> Free Plan Seat Limit</h3>
                    <p className="text-muted-foreground text-xs mb-3">Warning dialog shown on the Invite Team page when a Free plan workspace (5-seat limit) tries to invite a 6th teammate. Prompts the user to upgrade.</p>
                    <FreePlanSeatLimitDialogPreview />
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><StickyNote className="h-4 w-4" /> Free Plan Recording Retention</h3>
                    <p className="text-muted-foreground text-xs mb-3">Banner shown at the top of the Meetings &amp; Notes tab on the Free plan, letting users know recordings are kept for 90 days and prompting an upgrade for full history.</p>
                    <FreePlanRetentionBannerPreview />
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><CreditCard className="h-4 w-4" /> Credit Limit Warning</h3>
                    <p className="text-muted-foreground text-xs mb-3">Shown when a workspace is out of credits for the month. Free &amp; Tall plans see both an upgrade CTA and one-time credit packs; Grande (top plan) sees credit packs only.</p>
                    <CreditLimitWarningDialogPreview />
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><CreditCard className="h-4 w-4" /> Credit Balance Banners</h3>
                    <p className="text-muted-foreground text-xs mb-3">Global top banners shown workspace-wide: the amber low-credit warning at 15% remaining, and the red out-of-credits banner at zero. Use the triggers below to activate either one across the full app chrome so you can see it in context — click the × on the banner or "Clear" here to dismiss.</p>
                    <CreditBalanceBannerPreview />
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><PlugZap className="h-4 w-4" /> Free Plan Integrations</h3>
                    <p className="text-muted-foreground text-xs mb-3">Workspace Settings → Integrations as seen on the Free plan. Only Zapier is available; everything else is locked behind an upgrade prompt that opens the standard upgrade dialog.</p>
                    <FreePlanIntegrationsPreview />
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Inbox className="h-4 w-4" /> Empty States</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview the empty-state screens for each main left-nav tab. Opens with <code className="text-[11px] bg-muted px-1 py-0.5 rounded">?empty=1</code>.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" asChild><a href="/notes?empty=1" target="_blank" rel="noopener noreferrer">Meetings &amp; Notes</a></Button>
                      <Button variant="outline" size="sm" asChild><a href="/tasks?empty=1" target="_blank" rel="noopener noreferrer">Tasks</a></Button>
                      <Button variant="outline" size="sm" asChild><a href="/deals?empty=1" target="_blank" rel="noopener noreferrer">Deals</a></Button>
                      <Button variant="outline" size="sm" asChild><a href="/favorites?empty=1" target="_blank" rel="noopener noreferrer">Favorites</a></Button>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2">People — connect-account empty state by login provider:</p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" asChild className="gap-1.5">
                          <a href="/people?empty=1&provider=google" target="_blank" rel="noopener noreferrer">
                            <img src="/logos/google.svg" alt="" className="h-3.5 w-3.5" />
                            People (Google)
                          </a>
                        </Button>
                        <Button variant="outline" size="sm" asChild className="gap-1.5">
                          <a href="/people?empty=1&provider=microsoft" target="_blank" rel="noopener noreferrer">
                            <img src="/logos/microsoft.svg" alt="" className="h-3.5 w-3.5" />
                            People (Microsoft)
                          </a>
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2">Companies — connect-account empty state by login provider:</p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" asChild className="gap-1.5">
                          <a href="/companies?empty=1&provider=google" target="_blank" rel="noopener noreferrer">
                            <img src="/logos/google.svg" alt="" className="h-3.5 w-3.5" />
                            Companies (Google)
                          </a>
                        </Button>
                        <Button variant="outline" size="sm" asChild className="gap-1.5">
                          <a href="/companies?empty=1&provider=microsoft" target="_blank" rel="noopener noreferrer">
                            <img src="/logos/microsoft.svg" alt="" className="h-3.5 w-3.5" />
                            Companies (Microsoft)
                          </a>
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2">Today — connect-account empty state by login provider:</p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" asChild className="gap-1.5">
                          <a href="/?empty=1&provider=google" target="_blank" rel="noopener noreferrer">
                            <img src="/logos/google.svg" alt="" className="h-3.5 w-3.5" />
                            Today (Google)
                          </a>
                        </Button>
                        <Button variant="outline" size="sm" asChild className="gap-1.5">
                          <a href="/?empty=1&provider=microsoft" target="_blank" rel="noopener noreferrer">
                            <img src="/logos/microsoft.svg" alt="" className="h-3.5 w-3.5" />
                            Today (Microsoft)
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Share2 className="h-4 w-4" /> Shared Briefing</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview the shared meeting briefing page.</p>
                    <Button variant="outline" size="sm" asChild><a href="/shared/meeting/demo-meeting-123" target="_blank" rel="noopener noreferrer">Open Shared Briefing</a></Button>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Rocket className="h-4 w-4" /> New User Onboarding</h3>
                    <p className="text-muted-foreground text-xs mb-3">
                      Walk through the first-day experience for a brand-new user. The Today, Meetings &amp; Notes, and Deals tabs are pre-populated with a small set of clearly-tagged
                      <code className="text-[11px] bg-muted px-1 py-0.5 rounded mx-1">Test Deal</code>
                      and
                      <code className="text-[11px] bg-muted px-1 py-0.5 rounded mx-1">Test Meeting</code>
                      rows (via <code className="text-[11px] bg-muted px-1 py-0.5 rounded">?onboarding=1</code>) so the workspace doesn&apos;t look empty. The guided tour link below also runs the expanded product tour, which now includes drill-downs into a meeting and a deal.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          sessionStorage.setItem("coffee_product_tour", "true");
                          window.open("/?onboarding=1", "_blank", "noopener,noreferrer");
                        }}
                      >
                        Start Onboarding Tour
                      </Button>
                      <Button variant="outline" size="sm" asChild><a href="/?onboarding=1" target="_blank" rel="noopener noreferrer">Today (Onboarding)</a></Button>
                      <Button variant="outline" size="sm" asChild><a href="/notes?onboarding=1" target="_blank" rel="noopener noreferrer">Meetings &amp; Notes (Onboarding)</a></Button>
                      <Button variant="outline" size="sm" asChild><a href="/deals?onboarding=1" target="_blank" rel="noopener noreferrer">Deals (Onboarding)</a></Button>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><RotateCcw className="h-4 w-4" /> First-time Sync Progress</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview the email + calendar sync banner shown to first-time users in the left sidebar. Still in design — not currently mounted in the app.</p>
                    <Button variant="outline" size="sm" asChild><a href="/preview/sync-progress" target="_blank" rel="noopener noreferrer">Open Sync Progress Preview</a></Button>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Download className="h-4 w-4" /> Desktop Recorder Download</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview how the Coffee Desktop Recorder download row appears for a Mac user vs a Windows user.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" asChild><a href="/preview/desktop-download/mac" target="_blank" rel="noopener noreferrer">Mac Download Screen</a></Button>
                      <Button variant="outline" size="sm" asChild><a href="/preview/desktop-download/windows" target="_blank" rel="noopener noreferrer">Windows Download Screen</a></Button>
                      <Button variant="outline" size="sm" asChild><a href="/preview/desktop-download" target="_blank" rel="noopener noreferrer">Both (Side by Side)</a></Button>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                     <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Mail className="h-4 w-4" /> Work Email Required</h3>
                     <p className="text-muted-foreground text-xs mb-3">Blocking screen shown when someone tries to start a Coffee trial with a personal email (Gmail, Yahoo, Outlook, iCloud, etc). A work email tied to a company domain is required to provision the workspace.</p>
                     <div className="flex flex-wrap gap-2">
                       <Button variant="outline" size="sm" asChild>
                         <a href="/preview/work-email-required?email=jane.doe@gmail.com" target="_blank" rel="noopener noreferrer">Open (Gmail example)</a>
                       </Button>
                       <Button variant="outline" size="sm" asChild>
                         <a href="/preview/work-email-required" target="_blank" rel="noopener noreferrer">Open (no email)</a>
                       </Button>
                     </div>
                   </div>
                   <div className="h-px bg-border" />
                   <div>
                     <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Toasts</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview toast notification styles.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => toast.success("Record saved successfully")} className="gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-500" />Success</Button>
                      <Button variant="outline" size="sm" onClick={() => toast.error("Import failed — the CSV file could not be processed.")} className="gap-1.5"><X className="h-3.5 w-3.5 text-destructive" />Error</Button>
                      <Button variant="outline" size="sm" onClick={() => toast.warning("CRM sync is taking longer than expected.")} className="gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />Warning</Button>
                      <Button variant="outline" size="sm" onClick={() => toast.info("Your weekly pipeline summary is ready.")}>Info</Button>
                      <Button variant="outline" size="sm" onClick={() => toast("Meeting with HubSpot ended 30 min ago.", { description: "A follow-up email to Maria Garcia is ready for your review." })}>With Description</Button>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Megaphone className="h-4 w-4" /> New Feature Banner</h3>
                    <p className="text-muted-foreground text-xs mb-3">Live preview of the banner shown at the top of every page. Edit the copy in <code className="text-[11px] bg-muted px-1 py-0.5 rounded">AppLayout.tsx</code>.</p>
                    <div className="rounded-md border border-border overflow-hidden mb-3">
                      <div className="relative w-full overflow-hidden px-4 py-2 bg-[linear-gradient(90deg,hsl(340_80%_92%),hsl(320_60%_90%),hsl(280_50%_91%),hsl(260_45%_90%))] border-b border-[hsl(320_40%_85%)] dark:bg-[linear-gradient(90deg,hsl(340_40%_20%),hsl(320_30%_18%),hsl(280_25%_19%),hsl(260_25%_18%))] dark:border-[hsl(320_20%_30%)]">
                        <div className="relative flex items-center justify-center gap-2 pr-8">
                          <p className="text-xs text-foreground/90">
                            <span className="font-semibold">☕ Brew Notes</span>
                            <span className="ml-1.5">See what's new in June - Meeting Clips, Formula Fields and List Folders -</span>
                            {" "}
                            <span className="font-semibold underline underline-offset-2">See more</span>
                          </p>
                        </div>
                        <button
                          type="button"
                          className="absolute top-1/2 -translate-y-1/2 right-3 rounded-md p-0.5 text-foreground/50"
                          aria-label="Dismiss preview"
                          disabled
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => {
                      Object.keys(localStorage).filter(k => k.startsWith("feature-banner-dismissed-")).forEach(k => localStorage.removeItem(k));
                      window.dispatchEvent(new Event(FEATURE_BANNER_RESET_EVENT));
                      toast.success("Banner reset — it will appear at the top of every page now.");
                    }}>Reset Dismissed State</Button>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><PlugZap className="h-4 w-4" /> Disconnected Account Banner</h3>
                    <p className="text-muted-foreground text-xs mb-3">
                      Shown at the top of every page when a previously-connected account is
                      disconnected. Personal accounts (Google/Microsoft) use destructive
                      framing — email/calendar/recording all break. Workspace integrations
                      (CRM, Slack) use warning framing and are admin-only.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDisconnectedAccountPreview("google")}>
                        <img src="/logos/google.svg" alt="" className="h-3.5 w-3.5" />
                        Show Google disconnected
                      </Button>
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDisconnectedAccountPreview("microsoft")}>
                        <img src="/logos/microsoft.svg" alt="" className="h-3.5 w-3.5" />
                        Show Microsoft disconnected
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDisconnectedAccountPreview("crm-hubspot")}>
                        <img src="/logos/hubspot-color.svg" alt="" className="h-3.5 w-3.5" />
                        Show HubSpot disconnected
                      </Button>
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDisconnectedAccountPreview("crm-salesforce")}>
                        <img src="/logos/salesforce-color.svg" alt="" className="h-3.5 w-3.5" />
                        Show Salesforce disconnected
                      </Button>
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDisconnectedAccountPreview("slack")}>
                        <img src="/logos/slack.svg" alt="" className="h-3.5 w-3.5" />
                        Show Slack disconnected
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDisconnectedAccountPreview(["google", "crm-hubspot", "slack"])}>
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Show multiple disconnected
                      </Button>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setDisconnectedAccountPreview(null)}>Hide banner</Button>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Users className="h-4 w-4" /> Choose Workspace</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview the workspace selection screen shown when existing workspaces are detected after login.</p>
                    <Button onClick={() => window.open("/choose-workspace", "_blank")} variant="outline" size="sm">Open Choose Workspace</Button>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Rocket className="h-4 w-4" /> Onboarding</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview the full-screen new user onboarding flow.</p>
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Owner / Admin</p>
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={() => window.open("/onboarding", "_blank")} variant="outline" size="sm" className="gap-1.5">
                          <img src="/logos/google.svg" alt="Google" className="h-3.5 w-3.5" />
                          Google Account
                        </Button>
                        <Button onClick={() => window.open("/onboarding?provider=microsoft", "_blank")} variant="outline" size="sm" className="gap-1.5">
                          <img src="/logos/microsoft.svg" alt="Microsoft" className="h-3.5 w-3.5" />
                          Microsoft Account
                        </Button>
                      </div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mt-3">Member</p>
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={() => window.open("/onboarding?role=member", "_blank")} variant="outline" size="sm" className="gap-1.5">
                          <img src="/logos/google.svg" alt="Google" className="h-3.5 w-3.5" />
                          Google Account
                        </Button>
                        <Button onClick={() => window.open("/onboarding?role=member&provider=microsoft", "_blank")} variant="outline" size="sm" className="gap-1.5">
                          <img src="/logos/microsoft.svg" alt="Microsoft" className="h-3.5 w-3.5" />
                          Microsoft Account
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Rocket className="h-4 w-4" /> Product Tour</h3>
                    <p className="text-muted-foreground text-xs mb-3">Trigger the guided product tour that highlights Meetings, Tasks, Companies, and People.</p>
                    <Button
                      onClick={() => {
                        sessionStorage.setItem("coffee_product_tour", "true");
                        window.location.href = "/";
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Start Product Tour
                    </Button>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><CreditCard className="h-4 w-4" /> Subscription Expired</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview the expired trial or subscription reactivation flow with plan selection and team management.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => window.open("/subscription-expired?type=trial&date=March 10, 2026", "_blank")} variant="outline" size="sm">
                        Trial Expired
                      </Button>
                      <Button onClick={() => window.open("/subscription-expired?type=subscription&date=March 1, 2026", "_blank")} variant="outline" size="sm">
                        Subscription Expired
                      </Button>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Settings className="h-4 w-4" /> Settings by Role</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview how the Settings page looks for each role. Owners see everything, Admins see all except Billing, Members see only Account.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => window.open("/settings?role_preview=owner", "_blank")} variant="outline" size="sm">
                        Owner View
                      </Button>
                      <Button onClick={() => window.open("/settings?role_preview=admin", "_blank")} variant="outline" size="sm">
                        Admin View
                      </Button>
                      <Button onClick={() => window.open("/settings?role_preview=member", "_blank")} variant="outline" size="sm">
                        Member View
                      </Button>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Settings className="h-4 w-4" /> Accounts by Provider</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview the Accounts settings page for each sign-in provider. Only the matching calendar provider is shown.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => window.open("/settings?section=accounts&provider=google", "_blank")} variant="outline" size="sm" className="gap-1.5">
                        <img src="/logos/google.svg" alt="Google" className="h-3.5 w-3.5" />
                        Google Account
                      </Button>
                      <Button onClick={() => window.open("/settings?section=accounts&provider=microsoft", "_blank")} variant="outline" size="sm" className="gap-1.5">
                        <img src="/logos/microsoft.svg" alt="Microsoft" className="h-3.5 w-3.5" />
                        Microsoft Account
                      </Button>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Send className="h-4 w-4" /> Customer Emails</h3>
                    <p className="text-muted-foreground text-xs mb-3">Preview the visual email templates sent to customers.</p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Trial Period – Successful</p>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("welcome")}>Welcome</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("initial-sync-google")} className="gap-1.5">
                            <img src="/logos/google.svg" alt="" className="h-3.5 w-3.5" />Synced
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("initial-sync-microsoft")} className="gap-1.5">
                            <img src="/logos/microsoft.svg" alt="" className="h-3.5 w-3.5" />Synced
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("first-meeting")}>First Meeting</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("pipeline-changes")}>Pipeline Changes</Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5 mt-3">Trial Period – Unsuccessful</p>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("not-synced-google")} className="gap-1.5">
                            <img src="/logos/google.svg" alt="" className="h-3.5 w-3.5" />Not Synced
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("not-synced-microsoft")} className="gap-1.5">
                            <img src="/logos/microsoft.svg" alt="" className="h-3.5 w-3.5" />Not Synced
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("no-meeting-recording")}>No Meeting Recording</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("no-deals")}>No Deals</Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5 mt-3">Sync Completed</p>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("salesforce-sync")} className="gap-1.5">
                            <img src="/logos/salesforce-color.svg" alt="" className="h-3.5 w-3.5" />Salesforce
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("hubspot-sync")} className="gap-1.5">
                            <img src="/logos/hubspot-color.svg" alt="" className="h-3.5 w-3.5" />HubSpot
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("stripe-sync")} className="gap-1.5">
                            <img src="/logos/stripe.png" alt="" className="h-3.5 w-3.5 object-contain" />Stripe
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("quickbooks-sync")} className="gap-1.5">
                            <img src="/logos/quickbooks.svg" alt="" className="h-3.5 w-3.5" />QuickBooks
                          </Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5 mt-3">Sync Stopped</p>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("sync-stopped-google")} className="gap-1.5">
                            <img src="/logos/google.svg" alt="" className="h-3.5 w-3.5" />Google
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("sync-stopped-microsoft")} className="gap-1.5">
                            <img src="/logos/microsoft.svg" alt="" className="h-3.5 w-3.5" />Microsoft
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("sync-stopped-zoom")} className="gap-1.5">
                            <img src="/logos/zoom.svg" alt="" className="h-3.5 w-3.5" />Zoom
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("sync-stopped-salesforce")} className="gap-1.5">
                            <img src="/logos/salesforce-color.svg" alt="" className="h-3.5 w-3.5" />Salesforce
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("sync-stopped-hubspot")} className="gap-1.5">
                            <img src="/logos/hubspot-color.svg" alt="" className="h-3.5 w-3.5" />HubSpot
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("sync-stopped-stripe")} className="gap-1.5">
                            <img src="/logos/stripe.png" alt="" className="h-3.5 w-3.5 object-contain" />Stripe
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("sync-stopped-quickbooks")} className="gap-1.5">
                            <img src="/logos/quickbooks.svg" alt="" className="h-3.5 w-3.5" />QuickBooks
                          </Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5 mt-3">Notifications</p>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("invited")}>Invited</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("video-viewed")}>Recording Viewed</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("aws-downtime")}>Downtime</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("service-restored")}>Service Restored</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("pipeline-cleanup")}>Pipeline Cleanup</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("daily-prep")}>Daily Prep</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("daily-followups")}>Daily Follow-Ups</Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5 mt-3">Updates</p>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("changelog-may")}>Brew Notes May, 2026</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("changelog-june")}>Brew Notes June, 2026</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("changelog-july")}>Brew Notes July, 2026</Button>
                          <Button variant="outline" size="sm" onClick={() => onPreviewEmail("changelog-august")}>Brew Notes August, 2026</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="h-px bg-border" />
                </TabsContent>
  );
}
